import React, { useEffect, useRef, useState } from "react";
import { fromArrayBuffer } from "geotiff";
import { api } from "../../lib/api";
import { Loader2 } from "lucide-react";

// Mappa colori tipo "heatmap" per l'irraggiamento annuo (kWh/kW/anno)
function colorFor(t) {
  // t in [0,1] -> blu(basso) -> verde -> giallo -> rosso(alto)
  const stops = [
    [30, 60, 180], [40, 170, 120], [240, 220, 40], [230, 60, 30],
  ];
  const seg = Math.min(2, Math.floor(t * 3));
  const local = t * 3 - seg;
  const a = stops[seg], b = stops[seg + 1] || stops[seg];
  return [
    Math.round(a[0] + (b[0] - a[0]) * local),
    Math.round(a[1] + (b[1] - a[1]) * local),
    Math.round(a[2] + (b[2] - a[2]) * local),
  ];
}

export default function FluxPreview({ annualFluxUrl }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/solar/geotiff", { params: { url: annualFluxUrl }, responseType: "arraybuffer" });
        const tiff = await fromArrayBuffer(data);
        const image = await tiff.getImage();
        const w = image.getWidth();
        const h = image.getHeight();
        const rasters = await image.readRasters({ interleave: true });
        const band = rasters;
        let min = Infinity, max = -Infinity;
        for (let i = 0; i < band.length; i++) {
          const v = band[i];
          if (v > 0 && v < 3000) { if (v < min) min = v; if (v > max) max = v; }
        }
        if (cancelled) return;
        const canvas = canvasRef.current;
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        const img = ctx.createImageData(w, h);
        for (let i = 0; i < band.length; i++) {
          const v = band[i];
          const valid = v > 0 && v < 3000;
          const t = valid ? Math.max(0, Math.min(1, (v - min) / ((max - min) || 1))) : 0;
          const [r, g, b] = colorFor(t);
          img.data[i * 4] = r;
          img.data[i * 4 + 1] = g;
          img.data[i * 4 + 2] = b;
          img.data[i * 4 + 3] = valid ? 255 : 0;
        }
        ctx.putImageData(img, 0, 0);
        if (Number.isFinite(min)) setRange([Math.round(min), Math.round(max)]);
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setError("Heatmap non disponibile"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [annualFluxUrl]);

  return (
    <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: "var(--border)", background: "#0b1020" }} data-testid="flux-preview">
      {loading && <div className="absolute inset-0 flex items-center justify-center text-white text-xs"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento heatmap…</div>}
      {error && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs">{error}</div>}
      <canvas ref={canvasRef} className="w-full h-auto block" style={{ imageRendering: "auto" }} />
      {!loading && !error && (
        <>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-testid="flux-center-marker" title="Abitazione">
            <div className="w-5 h-5 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.5)]" />
          </div>
          {range && (
            <div className="absolute left-2 bottom-2 right-2 sm:right-auto flex items-center gap-2 text-[11px] text-white px-2 py-1 rounded-md" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="flux-legend">
              <span>{range[0]}</span>
              <span className="inline-block h-2 w-28 rounded-full" style={{ background: "linear-gradient(90deg,#1e3cb4,#28aa78,#f0dc28,#e63c1e)" }} />
              <span>{range[1]} kWh/kW·anno</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
